import axios from 'axios';

const API_HOST = process.env.REACT_APP_API_HOST;

export type AllowanceKey =
  | 'paid_holiday'
  | 'unpaid_holiday'
  | 'sick_leave'
  | 'working_remotely'
  | 'parental_leave'
  | 'other_leave';

export type LeaveType = {
  id?: string;
  /** Stored on requests as their type; never changes. */
  key: string;
  name: string;
  colour: string;
  paid: boolean;
  counts_against_allowance: boolean;
  allowance_key: AllowanceKey | null;
  requires_approval: boolean;
  allows_part_days: boolean;
  requires_note: boolean;
  active: boolean;
  sort_order: number;
  is_default: boolean;
};

export type LeaveTypeInput = Partial<
  Omit<LeaveType, 'id' | 'key' | 'is_default'>
>;

export type PublicHoliday = {
  id: string;
  date: string;
  name: string;
  office_ids: string[];
  all_offices: boolean;
  half_day: boolean;
  source?: string | null;
};

export type PublicHolidayInput = {
  date?: string;
  name?: string;
  office_ids?: string[];
  all_offices?: boolean;
  half_day?: boolean;
};

export type HolidayTemplate = {
  id: string;
  name: string;
  source: string;
  years: number[];
};

export type LeaveSettings = {
  working_days: number[];
  carryover: {
    enabled: boolean;
    cap_days: number;
    expiry: { month: number; day: number } | null;
  };
};

export type CarryoverRun = {
  from_year: number;
  to_year: number;
  cap_days: number;
  expires_at: string | null;
  dry_run: boolean;
  lines: Array<{
    user: string;
    name: string;
    allowed: number;
    carried_in: number;
    used: number;
    unused: number;
    carried_over: number;
  }>;
};

async function getLeaveTypes(includeInactive = false): Promise<LeaveType[]> {
  const { data } = await axios.get(`${API_HOST}/v1/leave-types`, {
    params: includeInactive ? { include_inactive: 'true' } : undefined,
  });
  return data?.data ?? [];
}

async function createLeaveType(input: LeaveTypeInput): Promise<LeaveType> {
  const { data } = await axios.post(`${API_HOST}/v1/leave-types`, input);
  return data?.data;
}

async function updateLeaveType(
  id: string,
  input: LeaveTypeInput,
): Promise<LeaveType> {
  const { data } = await axios.patch(`${API_HOST}/v1/leave-types/${id}`, input);
  return data?.data;
}

async function deleteLeaveType(id: string) {
  await axios.delete(`${API_HOST}/v1/leave-types/${id}`);
}

async function getHolidays(params: {
  from?: string;
  to?: string;
  office?: string;
} = {}): Promise<PublicHoliday[]> {
  const { data } = await axios.get(`${API_HOST}/v1/holidays`, { params });
  return data?.data ?? [];
}

async function getHolidayTemplates(): Promise<HolidayTemplate[]> {
  const { data } = await axios.get(`${API_HOST}/v1/holidays/templates`);
  return data?.data ?? [];
}

async function createHoliday(input: PublicHolidayInput): Promise<PublicHoliday> {
  const { data } = await axios.post(`${API_HOST}/v1/holidays`, input);
  return data?.data;
}

async function updateHoliday(
  id: string,
  input: PublicHolidayInput,
): Promise<PublicHoliday> {
  const { data } = await axios.patch(`${API_HOST}/v1/holidays/${id}`, input);
  return data?.data;
}

async function deleteHoliday(id: string) {
  await axios.delete(`${API_HOST}/v1/holidays/${id}`);
}

async function addHolidaysFromTemplate(input: {
  template: string;
  year: number;
  office_ids?: string[];
  all_offices?: boolean;
}): Promise<{ added: number; skipped: number }> {
  const { data } = await axios.post(
    `${API_HOST}/v1/holidays/from-template`,
    input,
  );
  return data?.data;
}

async function getLeaveSettings(): Promise<LeaveSettings> {
  const { data } = await axios.get(`${API_HOST}/v1/leave-settings`);
  return data?.data;
}

async function updateLeaveSettings(
  input: Partial<{
    working_days: number[];
    carryover: Partial<LeaveSettings['carryover']>;
  }>,
): Promise<LeaveSettings> {
  const { data } = await axios.patch(`${API_HOST}/v1/leave-settings`, input);
  return data?.data;
}

async function previewCarryover(year: number): Promise<CarryoverRun> {
  const { data } = await axios.get(`${API_HOST}/v1/carryover/preview`, {
    params: { year },
  });
  return data?.data;
}

async function runCarryover(year: number): Promise<CarryoverRun> {
  const { data } = await axios.post(`${API_HOST}/v1/carryover/run`, { year });
  return data?.data;
}

const leavePolicyApi = {
  getLeaveTypes,
  createLeaveType,
  updateLeaveType,
  deleteLeaveType,
  getHolidays,
  getHolidayTemplates,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  addHolidaysFromTemplate,
  getLeaveSettings,
  updateLeaveSettings,
  previewCarryover,
  runCarryover,
};

export default leavePolicyApi;
